import { formatDateWithTimeZone } from "@cp/toolkit";
import {
  type FetchResult,
  type MutationResult,
  type ScheduleSendInboxDraftMutation,
  useScheduleSendInboxDraftMutation,
} from "@qw/graphql";
import { Icon, useOpenConfirmation, useToast } from "@qw/ui";
import { useCallback } from "react";
import {
  type PendingAttachmentUploadsOptions,
  pendingAttachmentUploadsScheduleWarning,
} from "@/utils/pending-attachment-uploads";
import { useDraftsCompose } from "./use-drafts-compose";
import { useScheduleSendDraftForm } from "./use-schedule-send-draft-form";

type UseScheduleSendDraftReturn = [
  (
    id: string,
    options?: PendingAttachmentUploadsOptions
  ) => Promise<FetchResult<ScheduleSendInboxDraftMutation> | undefined>,
  MutationResult<ScheduleSendInboxDraftMutation>,
];

export const useScheduleSendDraft = (): UseScheduleSendDraftReturn => {
  const { toast } = useToast();
  const { closeDraft } = useDraftsCompose();
  const { openConfirmation } = useOpenConfirmation();
  const { openScheduleSendDraftForm } = useScheduleSendDraftForm();
  const [scheduleSendDraftMutation, result] = useScheduleSendInboxDraftMutation();

  const scheduleSendDraft = useCallback(
    async (id: string, { pendingAttachmentUploadCount = 0 }: PendingAttachmentUploadsOptions = {}) => {
      if (pendingAttachmentUploadCount > 0) {
        const confirmed = await openConfirmation({
          title: "Attachments still uploading",
          description: pendingAttachmentUploadsScheduleWarning(pendingAttachmentUploadCount),
          buttonProps: {
            theme: "primary",
            children: (
              <>
                <Icon icon="schedule_send" /> Continue
              </>
            ),
          },
        });

        if (!confirmed) {
          return;
        }
      }

      const scheduledSendAt = await openScheduleSendDraftForm();

      if (!scheduledSendAt) {
        return;
      }

      return scheduleSendDraftMutation({
        variables: { input: { id, scheduledSendAt } },
        onCompleted: (data) => {
          if (!data?.scheduleSendInboxDraft) {
            throw new Error("Failed to schedule draft for sending");
          }
          toast({
            title: "Draft scheduled",
            description: `Your draft has been scheduled for sending on ${formatDateWithTimeZone(
              scheduledSendAt,
              "LLL d, yyyy 'at' h:mm a"
            )}.`,
          });
          closeDraft(id);
        },
        onError: (error) => {
          toast({
            title: "Failed to schedule draft for sending",
            description: error instanceof Error ? error.message : "An unexpected error occurred",
            variant: "destructive",
          });
        },
        refetchQueries: ["InboxDrafts", "InboxDraft", "InboxDraftsCount", "InboxDraftsFailedScheduledSendsCount"],
      });
    },
    [scheduleSendDraftMutation, openConfirmation, openScheduleSendDraftForm, toast, closeDraft]
  );

  return [scheduleSendDraft, result];
};
